import React, {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import styled from 'styled-components';
import { ValidationMessages } from '../../../../core/utils/validationMessages';
import { Form } from '../../../../kit/Forms/Form';
import { FormGroup } from '../../../../kit/Forms/FormGroup';
import { TextInput } from '../../../../kit/Forms/TextInput';
import { Select } from '../../../../kit/Forms/Select';
import { Grid } from '../../../../kit/Grid';
import { OnboardingPageActions } from '../OnboardingPage';
import { NextButton, PageNavigationButtonContainer, PreviousButton } from './NavigationButtonContainer';
import { Text } from '../../../../kit/Text';
import {OidcProviderDto, OidcProviderType} from "server-openapi";
import {toasts} from "../../../../kit/Toasts/Toaster";
import {FacilityGroupOnlineService} from "../../../../core/mrs/Services";
import {LoginType} from "server-openapi/dist/generated-sdk/api";
import {SelectOption} from "../../../../kit/Forms/Select";

// NotUsed is only ever set behind the scenes for non Azure AD B2C providers, so it is not offered as a choice
const loginTypeOptions = Object.keys(LoginType)
    .filter((t) => t !== LoginType.NotUsed)
    .map((t) => ({ label: t, value: LoginType[t as keyof typeof LoginType] } as SelectOption<LoginType>));

interface IProps extends OnboardingPageActions {
  facilityGroupId: number | undefined;
  oidcProviders: OidcProviderDto[];
}

export interface FacilityManager {
  firstName?: string;
  lastName?: string;
  email?: string;
  loginType?: LoginType;
  oidcProviderId?: number;
}

async function onboardFacilityGroup(
    facilityManager: FacilityManager,
    loginType: LoginType,
    selectedFacilityGroupId?: number
): Promise<boolean> {
  if (!selectedFacilityGroupId || facilityManager.oidcProviderId === undefined) {
    toasts.error('Insufficient data to onboard facility.');
    return false;
  }

  try {
    await FacilityGroupOnlineService.onboard({
      facilityGroupId: selectedFacilityGroupId!,
      facilityGroupAdmin: {
        firstName: facilityManager.firstName || '',
        familyName: facilityManager.lastName || '',
        email: facilityManager.email || '',
        loginType,
        oidcProviderId: facilityManager.oidcProviderId
      },
    });
    toasts.success('Facility group onboarded successfully.');
  }
  catch (e) {
    toasts.error('Error onboarding facility.  Error is ' + e);
    return false;
  }

  return true;
}

export function InviteFacilityManagerPanel(props: IProps) {
  const { oidcProviders } = props;
  const form = useForm<FacilityManager>();

  const oidcProviderOptions = oidcProviders
      .filter((provider) => provider.id !== undefined)
      .map((provider) => ({
        label: provider.description ?? '',
        value: provider.id!
      } as SelectOption<number>));

  const defaultOidcProviderId = oidcProviders.find((provider) => provider.type === OidcProviderType.AzureAdb2C)?.id;
  const selectedOidcProviderId = form.watch('oidcProviderId');
  const isAzureAdb2CProviderSelected =
      oidcProviders.find((provider) => provider.id === selectedOidcProviderId)?.type === OidcProviderType.AzureAdb2C;

  // The providers load async, so default to the Azure AD B2C provider once they arrive (same as UserDetailsPage)
  useEffect(() => {
    if (form.getValues('oidcProviderId') == null && defaultOidcProviderId !== undefined) {
      form.setValue('oidcProviderId', defaultOidcProviderId);
    }
  }, [defaultOidcProviderId, form]);

  async function onSubmit(fm: FacilityManager) {
    // Login Type only applies to Azure AD B2C. The select is only shown (and so only required) for those
    // providers - every other provider gets NotUsed.
    const loginType = isAzureAdb2CProviderSelected ? fm.loginType! : LoginType.NotUsed;

    if (await onboardFacilityGroup(fm, loginType, props.facilityGroupId)) {
      props.goToNextPage();
    }
  }

  return (
    <>
      <Form form={form} onSubmit={onSubmit}>
        <InviteFacilityManagerContainer colsTemplate="350px 350px 350px" gap={1}>
          <FormGroup label="FIRST NAME" fullWidth>
            <TextInput
              name="firstName"
              fullWidth
              required
              autoComplete="off"
            />
          </FormGroup>
          <FormGroup label="LAST NAME" fullWidth>
            <TextInput
              name="lastName"
              fullWidth
              required
              autoComplete="off"
            />
          </FormGroup>
          <FormGroup label="EMAIL" fullWidth>
            <TextInput
              name="email"
              fullWidth
              required
              autoComplete="off"
              rules={{
                validate: (email: string) =>
                  ValidationMessages.email.regex!.test(email) || ValidationMessages.email.message,
              }}
            />
          </FormGroup>
          <FormGroup label="OIDC PROVIDER" fullWidth>
            <Select
                name="oidcProviderId"
                fullWidth
                required
                options={oidcProviderOptions}
                disabled={oidcProviderOptions.length === 0}
            />
          </FormGroup>
          {isAzureAdb2CProviderSelected && (
            <FormGroup label="LOGIN TYPE" fullWidth>
              <Select
                  name="loginType"
                  fullWidth
                  required
                  options={loginTypeOptions}
              />
            </FormGroup>
          )}
        </InviteFacilityManagerContainer>
        <PageNavigationButtonContainer>
          <PreviousButton onClick={props.goToPreviousPage}>Back</PreviousButton>
          <NextButton type="submit">Next</NextButton>
        </PageNavigationButtonContainer>
      </Form>
    </>
  );
}

const InviteFacilityManagerContainer = styled(Grid)`
  background: ${(p) => p.theme.backgrounds.lighter.bg};
  padding: 20px;
`;
